import React, { Component } from 'react'
import PropTypes from 'prop-types'

// Core
import CircularProgress from '@eig-builder/core-ui/CircularProgress'
import InjectTracker from '../../../../core/tracker'

// Components
import CartItemComponent from '../cart-item'
import CartSummaryComponent from '../../containers/cart-summary'
import PickupAddressAndInstructionsComponent from '../pickup-address-and-instructions'

// Helpers
import {
  globalInstanceJwt,
  addQueryParamtersToUrl,
  getQueryParametersFromUrl
} from '@eig-builder/core-utils/helpers/url-helper'
import SdkCommunicationHelper from '../../helpers/sdk-communication-helper'

// Other
import isEmpty from 'lodash/isEmpty'
import find from 'lodash/find'
import pick from 'lodash/pick'
import get from 'lodash/get'
import omit from 'lodash/omit'
import isNil from 'lodash/isNil'

import { getValidProducts } from '../../helpers/products-helper'
import FulfillmentTypes from '../../../../core/enums/fulfillment-types'

import Text from 'localize'
import './lang'

import '../../style/style.scss'

class CartComponent extends Component {
  static propTypes = {
    items: PropTypes.array,
    itemsWithProductInfo: PropTypes.array,
    cartHash: PropTypes.string,
    checkoutUrlRetrieving: PropTypes.bool,
    actions: PropTypes.object,
    productInfoReceiving: PropTypes.bool,
    productInfoError: PropTypes.object,
    getStoreSettingsRetrieving: PropTypes.bool.isRequired,
    getStoreSettingsResponse: PropTypes.object.isRequired,
    getStoreSettingsError: PropTypes.object,
    location: PropTypes.object,
    isTestMode: PropTypes.bool,
    insightsTrackingId: PropTypes.string,
    insightsUserId: PropTypes.string,
    insightsSessionId: PropTypes.string,
    checkoutWindow: PropTypes.object,
    tracker: PropTypes.string,
    pickupInstruction: PropTypes.string,
    pickupAddress: PropTypes.object
  }

  static defaultProps = {
    isTestMode: false
  }

  static contextTypes = {
    localize: PropTypes.func.isRequired,
    router: PropTypes.object
  }

  constructor (props) {
    super(props)
    this.checkoutWindowRef = null
    this.checkoutUrl = null
    this.queryParams =
      this.props.location && this.props.location.search && getQueryParametersFromUrl(this.props.location.search)
  }

  componentDidMount () {
    this.sdkCommunicationHelper = new SdkCommunicationHelper()
    this.sdkCommunicationHelper.getSettings()
    this.sdkCommunicationHelper.getIdentity()
    this.checkIfProductInfo(this.props)

    if (isEmpty(this.props.getStoreSettingsResponse)) {
      this.props.actions.getStoreSettings()
    }
    this.sdkCommunicationHelper.cartFinishedLoading()
    window.setCheckoutLocation = this.setCheckoutLocation
  }

  componentWillUpdate (nextProps) {
    this.checkIfProductInfo(nextProps)
  }

  setCheckoutLocation = (loc) => {
    if (loc && loc === this.checkoutUrl) {
      this.checkoutWindowRef.location = loc
    }
  }

  checkIfProductInfo (props) {
    // Reset discount code when shopping cart is emptied
    if (isEmpty(props.itemsWithProductInfo) && !isEmpty(this.props.itemsWithProductInfo)) {
      this.props.actions.clearDiscountCode()
    }

    // check if we have enough product info, otherwise we need to load more from the server.
    if (!isEmpty(props.itemsWithProductInfo) && props.productInfoReceiving !== true && props.loadMoreProductInfo) {
      const match = find(props.itemsWithProductInfo, item => isEmpty(omit(item.productInfo, 'showMaxStockWarning')))

      if (match) {
        this.props.actions.getProductInfoFromServer(props.items, props.cartHash)
      }
    }

    if (!isEmpty(props.checkoutUrl)) {
      const queryParams = pick(this.queryParams, ['buttonColor', 'buttonTextColor']) || {}
      queryParams.instanceJwt = globalInstanceJwt

      const newUrl = addQueryParamtersToUrl(props.checkoutUrl.replace('/v1.0', ''), queryParams)
      this.checkoutUrl = newUrl
      // this.checkoutWindowRef.location = newUrl
      // for automation purposes
      window.setCheckoutLocation && window.setCheckoutLocation(newUrl)

      this.props.actions.resetCheckoutUrl()
      // this.props.actions.clearDiscounts()
    }
  }

  validateFulfillmentMethod (cartItems) {
    cartItems.forEach(item => {
      if (isNil(item.fulfillmentMethodSelected)) {
        switch (item.productInfo.fulfillment_type) {
          case FulfillmentTypes.SHIPPABLE:
            item.fulfillmentMethodSelected = FulfillmentTypes.SHIPPABLE
            break
          case FulfillmentTypes.NONSHIPPABLE:
            item.fulfillmentMethodSelected = FulfillmentTypes.NONSHIPPABLE
            break
          case FulfillmentTypes.PICKUP:
            item.fulfillmentMethodSelected = FulfillmentTypes.PICKUP
            break
          case FulfillmentTypes.BOTH:
            // If fulfillmentMethodSelected does not exist, default it to shipping
            item.fulfillmentMethodSelected = FulfillmentTypes.SHIPPABLE
            break
          case FulfillmentTypes.DIGITALDOWNLOAD:
            item.fulfillmentMethodSelected = FulfillmentTypes.DIGITALDOWNLOAD
            break
        }
      }
    })
    return cartItems
  }

  componentWillUnmount () {
    if (this.checkoutWindowRef) {
      this.checkoutWindowRef.close()
      this.checkoutWindowRef = null
    }
    this.sdkCommunicationHelper.destroy()
  }

  doCheckout = discountCodes => {
    const {
      isTestMode = false,
      insightsTrackingId,
      insightsUserId,
      insightsSessionId,
      actions = {}
    } = this.props
    const { startCheckoutSession = () => {} } = actions

    let { itemsWithProductInfo } = this.props

    itemsWithProductInfo = this.validateFulfillmentMethod(itemsWithProductInfo)

    this.checkoutWindowRef = window.open()

    const queryParams = pick(this.queryParams, [
      'customThankYou',
      'lpCampaignActivityId',
      'sourceId',
      'sourceType',
      'contactId'
    ])
    const cartClickTrack = {
      lpCampaignActivityId: queryParams.lpCampaignActivityId
        ? decodeURIComponent(queryParams.lpCampaignActivityId)
        : null,
      sourceId: queryParams.sourceId ? decodeURIComponent(queryParams.sourceId) : null,
      sourceType: queryParams.sourceType ? decodeURIComponent(queryParams.sourceType) : null,
      contactId: queryParams.contactId ? decodeURIComponent(queryParams.contactId) : null
    }
    startCheckoutSession(
      getValidProducts(itemsWithProductInfo),
      discountCodes,
      isTestMode,
      insightsTrackingId,
      insightsUserId,
      insightsSessionId,
      queryParams.customThankYou ? decodeURIComponent(queryParams.customThankYou) : null,
      cartClickTrack
    )
  }

  generateCustomStyle = params => {
    if (params) {
      const { titleColor, buttonTextColor, buttonColor, backgroundColor, textColor } = params

      if (titleColor || buttonTextColor || buttonColor || backgroundColor || textColor) {
        return (
          <style>
            {titleColor &&
              `
              .summary-title{
                color:${titleColor}
              }
            `}
            {buttonTextColor &&
              `
              .store-cart #cart-summary .cart-btn{
                color:${buttonTextColor} !important
              }
            `}
            {buttonColor &&
              `
              .store-cart #cart-summary .cart-btn{
                background-color:${buttonColor}
              }
              .store-cart a{
                color:${buttonColor} !important
              }
            `}
            {backgroundColor &&
              `
              main, footer{
                background-color:${backgroundColor}
              }
            `}
            {textColor &&
              `
              main, footer{
                color:${textColor}
              }
            `}
          </style>
        )
      }
    }
  }

  render () {
    const {
      itemsWithProductInfo,
      productInfoReceiving,
      productInfoError,
      getStoreSettingsRetrieving,
      getStoreSettingsResponse,
      getStoreSettingsError,
      items,
      tracker
    } = this.props

    if (productInfoReceiving || getStoreSettingsRetrieving) {
      return (
        <div className='store-cart'>
          <CircularProgress show centered />
        </div>
      )
    }

    if (!isEmpty(getStoreSettingsError) || !isEmpty(productInfoError)) {
      return (
        <div className='store-cart'>
          <div className='store-error-wrapper'>
            <Text message='modules.cart.components.shoppingCart.error' />
          </div>
        </div>
      )
    }

    const isCartEmpty = isEmpty(items)

    const currency = {
      code: get(getStoreSettingsResponse.supportedCurrencies, 'default.code')
    }

    const pickupOrMixedItemsPresent = itemsWithProductInfo.filter((product) => {
      const fulfillmentType = product.productInfo.fulfillment_type
      return (fulfillmentType === FulfillmentTypes.PICKUP || fulfillmentType === FulfillmentTypes.BOTH)
    })

    return (
      <div className='store-cart'>
        {this.queryParams && this.generateCustomStyle(this.queryParams)}
        <div className='cart-content-wrapper'>
          <header>
            <div className='title alternate-background-light highlight'>
              <Text message='modules.cart.components.shoppingCart.title' />
              <div className='close-icon' onClick={() => this.sdkCommunicationHelper.closeShoppingCart()}>
                <Text message='modules.cart.components.shoppingCart.continueShopping' />
              </div>
            </div>
          </header>
          <main className='mx-2'>
            {isCartEmpty && (
              <div>
                <Text message='modules.cart.components.shoppingCart.cartEmpty' />
              </div>
            )}
            {!isCartEmpty && (
              <div className='row'>
                <div className='col-12 cart-product-list'>
                  {itemsWithProductInfo.map((product, index) => {
                    return (
                      <CartItemComponent
                        key={index}
                        removeProductFromCart={this.props.actions.removeProductFromCart}
                        clearProductFromCart={this.props.actions.clearProductFromCart}
                        updateProductQuantity={this.props.actions.updateProductQuantity}
                        addProductToCart={this.props.actions.addProductToCart}
                        updateFulfillmentMethod={this.props.actions.updateFulfillmentMethod}
                        {...product}
                        currency={currency}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </main>
          <footer className='mx-2'>
            {!isCartEmpty && pickupOrMixedItemsPresent.length > 0 && this.props.pickupAddress && (
              <PickupAddressAndInstructionsComponent
                themeColors={pick(this.queryParams, ['backgroundColor', 'textColor']) || {}}
                pickupInstruction={this.props.pickupInstruction}
                pickupAddress={this.props.pickupAddress} />
            )}
            {isCartEmpty === false && (
              <div className='cart-product-summary'>
                <CartSummaryComponent
                  sdkCommunicationHelper={this.sdkCommunicationHelper}
                  currency={currency}
                  doCheckout={this.doCheckout}
                  cartContent={itemsWithProductInfo}
                />
              </div>
            )}
          </footer>
        </div>
        <InjectTracker trackerScript={tracker} />
      </div>
    )
  }
}

export default CartComponent
